import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Field, FieldArray, ErrorMessage } from 'formik';
import { Row, Col, Form, Modal, Spinner } from 'react-bootstrap';
import { HiChevronRight, HiXMark } from 'react-icons/hi2';

import axiosInstance from '../../utils/axios';
import AppLogo from '../AppLogo';
import CreatableComponent from '../utils/Creatable';
import { hasPermission } from '../../hooks/usePermission';
import { bookingSchema } from './BookingSchema';
import { getRole } from '../../utils/role';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserFarms } from '../../store/features/userFarmsSlice';
import { uploadImageToS3, getFileExtension } from '../../utils/s3Upload';
import { bookingTranslations } from '../../ui/src/translations';

const GenerateClassName = (errors, touched, index, fieldName) => {
  const hasError =
    errors?.samples?.[index]?.[fieldName] &&
    touched?.samples?.[index]?.[fieldName];
  const isSuccess = touched?.samples?.[index]?.[fieldName];

  return `form-control ${hasError
    ? 'border-2 border-danger'
    : isSuccess
      ? 'border-2 border-success'
      : ''
    }`;
};

const BookingForm = ({ onTestTypeChange, language }) => {
  const t = (key) =>
    bookingTranslations[language]?.[key] ??
    bookingTranslations.en[key] ??
    key;
  const { id: user_id } = useParams();
  const navigate = useNavigate();
  const role = getRole();
  const { partner } = useSelector((state) => state.partner);
  const { farms, loading: farmsLoading } = useSelector((state) => state.userFarms || { farms: [], loading: false });
    
  const [bookingSummary, setBookingSummary] = useState([]);
  const [show, setShow] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [cropOptions, setCropOptions] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchCrops = async () => {
      try {
        const response = await axiosInstance.get('/api/v2/recommendations/crops');
        if (response.data.success) {
          const formattedOptions = response.data.data.map((crop) => ({
            value: crop.name_en, 
            label: crop.name_en
          }));
          setCropOptions(formattedOptions);
        }
      } catch (error) {
        console.error("Failed to fetch crops", error);
      }
    };
    fetchCrops();
    if (user_id) {
      dispatch(fetchUserFarms({ id: user_id }));
    }
  }, [dispatch, user_id]);

  useEffect(() => {
    if (onTestTypeChange) {
      onTestTypeChange('SOIL');
    }
    return () => sessionStorage.removeItem('bookingData');
  }, [onTestTypeChange]);

  const sampleDetails = {
    test_type: 'SOIL',
    farm_id: '',
    farm_name: '',
    farm_size: '',
    farm_size_unit: 'Acres',
    previous_crop: '',
    next_crop: '',
    age: 0,
    sample_type: 'SOIL',
    comments: '',
    use_location_recommendation: false,
    collection_photos: [], mixing_photos: [], packaging_photos: [],
    ...(hasPermission('bookings:entity_id') && { entity_id: '' }),
    ...(hasPermission('bookings:treatment') && { treatment: '' }),
  };

  const waterSampleDetails = {
    test_type: 'WATER',
    sample_type: 'WATER',
    farm_id: '',
    farm_name: '',
    age: 0,
    comments: '',
    collection_photos: [],
    ...(hasPermission('bookings:entity_id') && { entity_id: '' }),
  };

  const handleSubmit = async (values, actions) => {
    // normalize next crop
    const formattedSamples = values.samples.map((s) => {
      const { collection_photos, mixing_photos, packaging_photos, ...rest } = s;
      
      if (rest.test_type === 'SOIL' && rest.next_crop) {
        rest.next_crop = rest.next_crop.split('(')[0].trim();
      }

      return {
        ...rest,
        sample_info: {
          collection_photos: collection_photos || [],
          mixing_photos: mixing_photos || [],
          packaging_photos: packaging_photos || []
        }
      };
    });

    const booking = {
      samples_count: formattedSamples.length,
      user_id,
      samples: formattedSamples,
      use_location_recommendation: values.use_location_recommendation
    };

    try {
      let response;
      if (role === 'FARMER') {
        response = await axiosInstance.post('user/booking', { data: booking });
      } else if (role === 'PARTNER' || role === 'TEAM_MEMBER') {
        response = await axiosInstance.post(`admin/booking`, { data: booking });
      } else if (role === 'FIELD_OFFICER') {
        response = await axiosInstance.post(`/field-officer/booking`, { data: booking });
      } else {
        response = await axiosInstance.post('super-admin/booking', { data: booking });
      }

      const ids = response.data.samples.map((s) => s.split(': ')[1]);

      setBookingSummary(
        formattedSamples.map((sample, i) => ({
          ...sample,
          sample_id: ids[i],
        }))
      );

      setShow(true);
      actions.resetForm();
      sessionStorage.removeItem('bookingData');
    } catch (err) {
      console.error('Booking failed', err);
    }
  };

  const handleCreateNewFarm = (values) => {
    sessionStorage.setItem('bookingData', JSON.stringify(values.samples));
    navigate(`/farmers/create-farms/${user_id}`);
  };

  const treatmentOptions = [
    { value: "Not Applicable", label: t("notApplicable") },
    { value: "Initial Control", label: t("initialControl") },
    { value: "Initial Treated", label: t("initialTreated") },
    { value: "Final Control", label: t("finalControl") },
    { value: "Final Treated", label: t("finalTreated") },
  ];

  const PhotoUploader = ({ label, fieldPath, currentUrls = [], setFieldValue, folder }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const handlePhotoCapture = async (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      setUploading(true);
      try {
        const uploadPromises = files.map(file => {
          const extension = getFileExtension(file);
          return uploadImageToS3(file, extension, folder);
        });

        const newUrls = await Promise.all(uploadPromises);
        const updatedUrls = [...currentUrls, ...newUrls];
        setFieldValue(fieldPath, updatedUrls);

      } catch (err) {
        console.error('Error uploading photos:', err);
        alert('Failed to upload some photos. Please try again.');
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };

    const removePhoto = (indexToRemove) => {
      const updatedUrls = currentUrls.filter((_, idx) => idx !== indexToRemove);
      setFieldValue(fieldPath, updatedUrls);
    };

    return (
      <div className="border rounded p-3 bg-white text-center h-100 d-flex flex-column justify-content-start align-items-center">
        <h6 className="text-muted fw-bold mb-3">{label}</h6>
        
        {/* Thumbnail Grid */}
        {currentUrls.length > 0 && (
          <div className="d-flex flex-wrap justify-content-center gap-2 mb-3 w-100">
            {currentUrls.map((url, idx) => (
              <div key={idx} className="position-relative" style={{ width: '70px', height: '70px' }}>
                <img 
                  src={url} 
                  alt={`${label} ${idx + 1}`} 
                  className="w-100 h-100 object-fit-cover rounded border border-secondary" 
                />
                <button
                  type="button"
                  onClick={() => removePhoto(idx)}
                  className="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                  style={{ width: '20px', height: '20px', transform: 'translate(30%, -30%)' }}
                  title={t("removePhoto")}
                >
                  <HiXMark size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Upload Button Area */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handlePhotoCapture}
          className="d-none"
          disabled={uploading}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="btn btn-outline-success w-100 py-2 mt-auto"
        >
          {uploading ? (
            <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2"/>{t("uploading")}</>
          ) : (
            currentUrls.length > 0 ? t("addMorePhotos") : t("takeUploadPhoto")
          )}
        </button>
      </div>
    );
  };

  return (
    <Formik
      initialValues={{
        samples: sessionStorage.getItem('bookingData')
          ? [
            JSON.parse(sessionStorage.getItem('bookingData'))[0],
            sampleDetails,
          ]
          : [sampleDetails],
      }}
      validationSchema={bookingSchema}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => {
        return (
          <Form className="form__details" onSubmit={handleSubmit} onChange={handleChange}>
            <div className="mb-4 d-flex gap-4">
              <Form.Check
                type="radio"
                label={t("soilTest")}
                checked={values.samples[0].test_type === 'SOIL'}
                onChange={() => {
                  setFieldValue('samples', [{ ...sampleDetails, test_type: 'SOIL' }]);
                  if (onTestTypeChange) onTestTypeChange('SOIL');
                }}
              />
              <Form.Check
                type="radio"
                label={t("waterTest")}
                checked={values.samples[0].test_type === 'WATER'}
                onChange={() => {
                  setFieldValue('samples', [{ ...waterSampleDetails }]);
                  if (onTestTypeChange) onTestTypeChange('WATER');
                }}
              />
            </div>

            <FieldArray name="samples">
              {(arrayHelpers) => (
                <>
                  {values.samples.map((sample, index) => (
                    <div key={index} className="mt-4 pb-3 position-relative border-bottom">
                      {index > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0"
                          style={{ zIndex: 10 }}
                          onClick={() => arrayHelpers.remove(index)}
                          title="Remove Sample"
                        >
                          <HiXMark size={18} />
                        </button>
                      )}
                      <Row className="g-4">
                        <Col md={1}>
                          <div className="form-control text-center fw-bold h-100 d-flex align-items-center justify-content-center">
                            S{index + 1}
                          </div>
                        </Col>

                        <Col md={11}>
                          <Row className="g-3">
                            {/* Farm Dropdown Selection */}
                            <Col md={12}>
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <Form.Label className="text-muted fw-semibold mb-0">{t("selectExistingFarm")}</Form.Label>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-decoration-none"
                                  onClick={() => handleCreateNewFarm(values)}
                                >
                                  {t("createNewFarm")}
                                </button>
                              </div>
                              <Field
                                as="select"
                                name={`samples[${index}].farm_id`}
                                className="form-select border-primary bg-light"
                                disabled={farmsLoading}
                                onChange={(e) => {
                                  const selectedFarmId = e.target.value;
                                  setFieldValue(`samples[${index}].farm_id`, selectedFarmId);
                                  if (selectedFarmId) {
                                    const selectedFarm = farms.find(f => f.farm_id === selectedFarmId || f.id === selectedFarmId);
                                    if (selectedFarm) {
                                      setFieldValue(`samples[${index}].farm_name`, selectedFarm.farm_name || selectedFarm.name || '');
                                      if (sample.test_type === 'SOIL') {
                                        setFieldValue(`samples[${index}].farm_size`, selectedFarm.farm_size || '');
                                        setFieldValue(`samples[${index}].farm_size_unit`, selectedFarm.farm_size_unit || 'Acres');
                                      }
                                    }
                                  }
                                }}
                              >
                                <option value="">{t("chooseFarm")}</option>
                                {farms && farms.map((farm) => (
                                  <option key={farm.farm_id || farm.id} value={farm.farm_id || farm.id}>
                                    {farm.farm_name || farm.name} 
                                    {farm.farm_size ? ` (${farm.farm_size} ${farm.farm_size_unit})` : ''}
                                  </option>
                                ))}
                              </Field>
                            </Col>

                            {sample.test_type === 'SOIL' && (
                              <Col md={12}>
                                <div className="d-flex align-items-center gap-3 p-3 rounded bg-light border mb-2 mt-2">
                                  <Form.Check
                                    type="switch"
                                    id={`location-rec-switch-${index}`}
                                    label={<span className="fw-bold">{t("enableLocationRecommendation")}</span>}
                                    name={`samples[${index}].use_location_recommendation`}
                                    checked={sample.use_location_recommendation}
                                    onChange={handleChange}
                                    style={{ fontSize: '1.1rem' }}
                                  />
                                  <small className="text-muted mb-0">
                                    {t("locationRecommendationHint")}
                                  </small>
                                </div>
                              </Col>
                            )}

                            {/* Farm name / Water Sample Name */}
                            <Col md={6}>
                              <div className="form-floating">
                                <Field
                                  name={`samples[${index}].farm_name`}
                                  className={GenerateClassName(errors, touched, index, 'farm_name')}
                                  placeholder={sample.test_type === 'WATER' ? t("waterSampleName") : t("farmName")}
                                />
                                <Form.Label>
                                  {sample.test_type === 'WATER' ? t("waterSampleName") : t("farmName")} <span className="text-danger">*</span>
                                </Form.Label>
                              </div>
                              <ErrorMessage className="form_errors text-danger" name={`samples[${index}].farm_name`} component="div" />
                            </Col>

                            {/* Comments */}
                            <Col md={6}>
                              <div className="form-floating">
                                <Field
                                  name={`samples[${index}].comments`}
                                  className={GenerateClassName(errors, touched, index, 'comments')}
                                  placeholder={t("comments")}
                                />
                                <Form.Label>{t("comments")}</Form.Label>
                              </div>
                            </Col>

                            {hasPermission('bookings:entity_id') && (
                              <Col md={6}>
                                <div className="form-floating">
                                  <Field
                                    name={`samples[${index}].entity_id`}
                                    className={GenerateClassName(errors, touched, index, 'entity_id')}
                                    placeholder={t("entityId")}
                                  />
                                  <Form.Label>{t("entityId")}</Form.Label>
                                </div>
                              </Col>
                            )}

                            {sample.test_type === 'SOIL' && (
                              <>
                                {/* Farm Size & Unit */}
                                <Col md={6}>
                                  <div className="input-group">
                                    <div className="form-floating" style={{ flex: '1 1 50%' }}>
                                      <Field
                                        type="number"
                                        step="0.01"
                                        name={`samples[${index}].farm_size`}
                                        className={GenerateClassName(errors, touched, index, 'farm_size')}
                                        placeholder={t("farmSize")}
                                        id={`farm-size-${index}`}
                                      />
                                      <Form.Label htmlFor={`farm-size-${index}`}>
                                        {t("farmSize")} <span className="text-danger">*</span>
                                      </Form.Label>
                                    </div>

                                    <Field
                                      as="select"
                                      name={`samples[${index}].farm_size_unit`}
                                      className={`input-group-text text-black ${errors.samples?.[index]?.farm_size_unit &&
                                        touched.samples?.[index]?.farm_size_unit
                                        ? "border-danger bg-danger text-white"
                                        : ""
                                        }`}
                                      style={{ flex: '1 1 50%', minWidth: '0' }}
                                    >
                                      {[
                                        "Acres", "Hectare", "Bigha", "Gaj", "Kanal", "Biswa",
                                        "Dismil", "Killa", "Marla", "Nali", "Guntha",
                                        "Square Karam", "Ankanam", "Gajam", "Chatak",
                                        "Dhur", "Perch", "Lessa", "Murabba", "Biswa Kacha", "Pura", "sq mtr"
                                      ].map((unit) => (
                                        <option key={unit} value={unit}>
                                          {unit}
                                        </option>
                                      ))}
                                    </Field>
                                  </div>
                                  <ErrorMessage
                                    className="form_errors text-danger"
                                    name={`samples[${index}].farm_size`}
                                    component="div"
                                  />
                                </Col>

                                {/* Previous crop */}
                                <Col md={6}>
                                  <div className="form-floating">
                                    <Field
                                      name={`samples[${index}].previous_crop`}
                                      className={GenerateClassName(errors, touched, index, 'previous_crop')}
                                      placeholder={t("previousCrop")}
                                    />
                                    <Form.Label>{t("previousCrop")} <span className="text-danger">*</span></Form.Label>
                                  </div>
                                  <ErrorMessage className="form_errors text-danger" name={`samples[${index}].previous_crop`} component="div" />
                                </Col>

                                {/* Treatment */}
                                {hasPermission('bookings:treatment') && (
                                  <Col md={6}>
                                    <div className="input-group">
                                      <span className="input-group-text">{t("treatment")}:</span>
                                      <Field
                                        as="select"
                                        name={`samples[${index}].treatment`}
                                        className="form-select"
                                      >
                                        {treatmentOptions.map((option) => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </Field>
                                    </div>
                                  </Col>
                                )}

                                {/* Next crop */}
                                <Col md={6}>
                                  {cropOptions.length > 0 ? (
                                    <CreatableComponent
                                      defaultOptions={cropOptions}
                                      placeholder={t("nextCrop")}
                                      updateVal={(val) =>
                                        setFieldValue(`samples[${index}].next_crop`, val)
                                      }
                                    />
                                  ) : (
                                    <div className="form-control text-muted">{t("loadingCrops")}</div>
                                  )}
                                  <ErrorMessage className="form_errors text-danger" name={`samples[${index}].next_crop`} component="div" />
                                </Col>
                                <Col md={6}>
                                  <div className="form-floating">
                                    <Field
                                      type="number"
                                      step="0.1"
                                      min="0"
                                      name={`samples[${index}].age`}
                                      className={GenerateClassName(errors, touched, index, 'age')}
                                      placeholder={t("treeAge")}
                                    />
                                    <Form.Label>{t("treeAge")} (Years)</Form.Label>
                                  </div>
                                  <ErrorMessage className="form_errors text-danger" name={`samples[${index}].age`} component="div" />
                                  <small className="text-muted d-block mt-1">
                                    {t("treeAgeNote")}
                                  </small>
                                </Col>
                              </>
                            )}
                            <Col md={12} className="mt-4">
                              <div className="p-3 bg-light rounded border border-light-subtle">
                                <h6 className="fw-bolder mb-3 text-success">
                                  {sample.test_type === 'WATER' ? t("waterBookingPhotos") : t("bookingPhotos")}
                                </h6>
                                
                                <Row className="g-3">
                                  <Col md={sample.test_type === 'WATER' ? 12 : 4}>
                                    <PhotoUploader
                                      label={sample.test_type === 'WATER' ? t("waterCollection") : t("sampleCollection")}
                                      fieldPath={`samples[${index}].collection_photos`} 
                                      currentUrls={sample.collection_photos || []}
                                      setFieldValue={setFieldValue}
                                      folder="collection-photos/digging"
                                      t={t}
                                    />
                                  </Col>

                                  {sample.test_type === 'SOIL' && (
                                    <>
                                      <Col md={4}>
                                        <PhotoUploader
                                          label={t("sampleMixing")}
                                          fieldPath={`samples[${index}].mixing_photos`} 
                                          currentUrls={sample.mixing_photos || []}
                                          setFieldValue={setFieldValue}
                                          folder="collection-photos/mixing"
                                          t={t}
                                        />
                                      </Col>
                                      <Col md={4}>
                                        <PhotoUploader
                                          label={t("samplePackaging")}
                                          fieldPath={`samples[${index}].packaging_photos`} 
                                          currentUrls={sample.packaging_photos || []}
                                          setFieldValue={setFieldValue}
                                          folder="collection-photos/bag"
                                          t={t}
                                        />
                                      </Col>
                                    </>
                                  )}
                                </Row>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </div>
                  ))}

                  <div className="mt-4">
                    <button
                      type="button"
                      className="btn btn-dark"
                      onClick={() => {
                        const currentType = values.samples[0]?.test_type || 'SOIL';
                        arrayHelpers.push(
                          currentType === 'SOIL'
                            ? { ...sampleDetails }
                            : { ...waterSampleDetails }
                        );
                      }}
                    >
                      {t("addSample")}
                    </button>
                  </div>

                  <div className="d-flex justify-content-end mt-4">
                    <button type="submit" className="btn btn-dark btn-lg px-5">
                      {t("submitBooking")} <HiChevronRight className="ms-2" />
                    </button>
                  </div>
                </>
              )}
            </FieldArray>

            {/* SUCCESS MODAL */}
            <Modal show={show} backdrop="static">
              <Modal.Header>
                <Modal.Title>
                  <AppLogo
                    partnerLogoUrl={(role === 'PARTNER' || role === 'TEAM_MEMBER') ? partner?.profile_picture : null}
                    alt="Soil Doctor Logo"
                    style={{ width: '180px', maxWidth: '40vw' }}
                  />
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <h5 className="mb-3">{t("bookingSuccessful")}</h5>
                {bookingSummary.map((s, i) => (
                  <div key={i} className="mb-2 p-2 bg-light border rounded">
                    <strong>{t("sample")} {i + 1} (S{i + 1})</strong><br />
                    {t("id")}: {s.sample_id}<br />
                    {t("farm")}: {s.farm_name}
                  </div>
                ))}
                <div className="mt-3">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => setIsChecked(!isChecked)}
                  />
                  <span className="ms-2">{t("notedDetails")}</span>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <button
                  className="btn btn-dark"
                  disabled={!isChecked}
                  onClick={() => {
                    if (role === 'FARMER' || role === 'FIELD_OFFICER') {
                      navigate('/bookings');
                    } else {
                      navigate('/users');
                    }
                  }}
                >
                  {t("close")}
                </button>
              </Modal.Footer>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};

export default BookingForm;